import { Checkbox, Col, Divider, Modal, Row, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

// Keys must match DetailColumns in MentenancePlanningReportDocumentHelper.cs.
const REPORT_EXPORT_COLUMNS = [
  { key: 'number', label: 'Nr. mentenanta' },
  { key: 'company', label: 'Beneficiar' },
  { key: 'workPlace', label: 'Punct de lucru' },
  { key: 'status', label: 'Status' },
  { key: 'assignedTechnician', label: 'Tehnician asignat' },
  { key: 'systemTypes', label: 'Sisteme' },
  { key: 'periodicity', label: 'Periodicitate' },
  { key: 'contractNumber', label: 'Nr. contract' },
  { key: 'appointment', label: 'Data programata' },
  { key: 'dateEnd', label: 'Termen limita' },
  { key: 'interventionFile', label: 'Fisa de interventie' },
  { key: 'interventionDate', label: 'Data interventie' },
  { key: 'interventionFileTechnicians', label: 'Tehnicieni pe fisa de interventie' },
];

const ALL_COLUMN_KEYS = REPORT_EXPORT_COLUMNS.map((column) => column.key);
const STORAGE_KEY = 'maintenanceReportExportColumns';

const loadSavedColumns = (): string[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    const knownColumns = Array.isArray(saved) ? ALL_COLUMN_KEYS.filter((key) => saved.includes(key)) : [];
    return knownColumns.length > 0 ? knownColumns : ALL_COLUMN_KEYS;
  } catch {
    return ALL_COLUMN_KEYS;
  }
};

interface MentenancePlanningReportExportModalProps {
  open: boolean;
  loading: boolean;
  periodLabel: string;
  onCancel: () => void;
  onConfirm: (columns: string[]) => void;
}

const MentenancePlanningReportExportModal: React.FC<MentenancePlanningReportExportModalProps> = ({
  open,
  loading,
  periodLabel,
  onCancel,
  onConfirm,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(loadSavedColumns);

  useEffect(() => {
    if (open) {
      setSelectedColumns(loadSavedColumns());
    }
  }, [open]);

  const isAllSelected = selectedColumns.length === ALL_COLUMN_KEYS.length;

  const handleConfirm = () => {
    const columns = ALL_COLUMN_KEYS.filter((key) => selectedColumns.includes(key));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
    onConfirm(columns);
  };

  return (
    <Modal
      open={open}
      title={`Descarca raport Excel - ${periodLabel}`}
      okText='Descarca'
      cancelText='Anuleaza'
      onOk={handleConfirm}
      onCancel={onCancel}
      confirmLoading={loading}
      okButtonProps={{ disabled: selectedColumns.length === 0 }}
    >
      <Typography.Paragraph type='secondary'>
        Raportul include toate mentenantele din luna selectata, indiferent de filtrele aplicate. Alege coloanele care
        apar in foaia "Mentenante"; foile "Sumar" si "Tehnicieni" sunt incluse mereu.
      </Typography.Paragraph>
      <Checkbox
        checked={isAllSelected}
        indeterminate={selectedColumns.length > 0 && !isAllSelected}
        onChange={(event) => setSelectedColumns(event.target.checked ? ALL_COLUMN_KEYS : [])}
      >
        Selecteaza toate ({selectedColumns.length}/{ALL_COLUMN_KEYS.length})
      </Checkbox>
      <Divider style={{ margin: '12px 0' }} />
      <Checkbox.Group
        value={selectedColumns}
        onChange={(values) => setSelectedColumns(values as string[])}
        style={{ width: '100%' }}
      >
        <Row gutter={[16, 8]} style={{ width: '100%' }}>
          {REPORT_EXPORT_COLUMNS.map((column) => (
            <Col key={column.key} xs={24} sm={12}>
              <Checkbox value={column.key}>{column.label}</Checkbox>
            </Col>
          ))}
        </Row>
      </Checkbox.Group>
      {selectedColumns.length === 0 && (
        <Typography.Text type='danger' style={{ display: 'block', marginTop: '12px' }}>
          Selecteaza cel putin o coloana.
        </Typography.Text>
      )}
    </Modal>
  );
};

export default MentenancePlanningReportExportModal;
